// NBA signing exceptions (mid-level / room / bi-annual), layered onto the apron module.
//
// Stock ZenGM's soft cap allows only two over-cap signings: Bird rights (re-signing your own
// player) and minimum contracts. The real NBA also lets teams sign outside free agents with a
// handful of capped exceptions, sized off the salary cap and gated by apron status. This module
// is the single source of truth for those exceptions; per-season usage is tracked in a ledger on
// teamSeason (sigExceptions), like the apron ledger.
//
// Rules modeled (2023 CBA, simplified):
// - Non-taxpayer MLE: signing must leave payroll at/below the 1st apron (the CBA's hard cap for
//   using it), up to 4 years.
// - Taxpayer MLE: smaller amount, signing must leave payroll at/below the 2nd apron, up to
//   2 years. Shares one per-season bucket with the non-taxpayer MLE — it's the same exception,
//   the limit just shrinks for expensive teams. Teams over the 2nd apron get nothing.
// - Room exception: for teams currently under the cap; may take them over it, up to 3 years.
//   Using it forgoes the MLE/BAE that season (and vice versa).
// - Bi-annual exception: like a mini-MLE, but unusable in consecutive seasons.
// Exceptions can be split across multiple signings (the ledger tracks the amount used). The
// CBA's season-long hard cap after using an exception is enforced only at signing time here —
// trades are already policed separately by the apron module's AI allergy + 2nd-apron matching.
//
// All amounts are in THOUSANDS of dollars, matching g.get("salaryCap") / getPayroll().

import { g, helpers } from "../../util/index.ts";
import { idb } from "../../db/index.ts";
import { apronsActive, apronThresholds } from "./aprons.ts";

export const EXCEPTION_CONFIG = {
	// First-year salary as a fraction of the salary cap, from the 2023 CBA. These reproduce the
	// published 2026-27 values (NT-MLE $15.044M, taxpayer $6.064M, room $9.366M, BAE $5.477M) and
	// scale automatically as the league cap grows.
	midLevelFrac: 0.0912,
	taxpayerMidLevelFrac: 0.0368,
	roomFrac: 0.05678,
	biannualFrac: 0.0332,

	// Max contract lengths (NBA: 4 / 2 / 3 / 2). ZenGM contracts are flat, so only the length is
	// constrained — there's no raise structure to model.
	maxYears: {
		mle: 4,
		taxpayerMle: 2,
		room: 3,
		bae: 2,
	},
};

export type SigningExceptionKind = "mle" | "taxpayerMle" | "room" | "bae";

// Per-season usage, stored on teamSeason.sigExceptions. taxpayerMle charges the mle bucket.
export type SigningExceptionLedger = {
	mle: number;
	room: number;
	bae: number;
};

export const EMPTY_EXCEPTION_LEDGER: SigningExceptionLedger = {
	mle: 0,
	room: 0,
	bae: 0,
};

export const EXCEPTION_NAMES: Record<SigningExceptionKind, string> = {
	mle: "Mid-Level Exception",
	taxpayerMle: "Taxpayer Mid-Level Exception",
	room: "Room Exception",
	bae: "Bi-Annual Exception",
};

// Same rounding fudge accept.ts uses for cap checks, to tolerate contract rounding.
const FUDGE = 1;

export const signingExceptionsActive = () => apronsActive();

// Full first-year amounts (thousands) at the current cap.
export const exceptionAmounts = () => {
	const cap = g.get("salaryCap");
	const c = EXCEPTION_CONFIG;
	return {
		mle: c.midLevelFrac * cap,
		taxpayerMle: c.taxpayerMidLevelFrac * cap,
		room: c.roomFrac * cap,
		bae: c.biannualFrac * cap,
	};
};

export type ExceptionSigningResult =
	| { kind: SigningExceptionKind }
	| { error: string };

// Can a team at `payroll` sign an outside free agent for `amount` using an exception? Pure
// eligibility math — the caller is responsible for only asking when the signing doesn't fit
// under the cap and isn't a minimum contract or Bird re-signing. When multiple exceptions fit,
// the smallest is charged, preserving the bigger tools for later signings.
export const evaluateExceptionSigning = ({
	payroll,
	amount,
	ledger,
	usedBaeLastSeason,
}: {
	payroll: number;
	amount: number;
	ledger: SigningExceptionLedger;
	usedBaeLastSeason: boolean;
}): ExceptionSigningResult => {
	if (!signingExceptionsActive()) {
		return { error: "Signing exceptions are not available in this league." };
	}

	const t = apronThresholds();
	const a = exceptionAmounts();
	const post = payroll + amount;
	const fmt = (x: number) => helpers.formatCurrency(x / 1000, "M", 1);

	if (payroll < g.get("salaryCap")) {
		// Under-cap team: room exception only. Committing to the room route forgoes the MLE/BAE
		// for the season, and using the MLE/BAE forgoes room.
		if (ledger.mle > 0 || ledger.bae > 0) {
			return {
				error:
					"You already used the Mid-Level or Bi-Annual Exception this season, so the Room Exception is unavailable.",
			};
		}
		const remaining = a.room - ledger.room;
		if (amount - FUDGE > remaining) {
			return {
				error: `This contract exceeds your remaining Room Exception (${fmt(Math.max(0, remaining))}).`,
			};
		}
		if (post - FUDGE > t.first) {
			return {
				error: `Signing this contract with the Room Exception would put your payroll over the first apron (${fmt(t.first)}).`,
			};
		}
		return { kind: "room" };
	}

	// Over-cap team: BAE and MLE.
	if (ledger.room > 0) {
		return {
			error:
				"You already used the Room Exception this season, so the Mid-Level and Bi-Annual Exceptions are unavailable.",
		};
	}

	if (
		!usedBaeLastSeason &&
		amount - FUDGE <= a.bae - ledger.bae &&
		post - FUDGE <= t.first
	) {
		return { kind: "bae" };
	}

	if (amount - FUDGE <= a.mle - ledger.mle && post - FUDGE <= t.first) {
		return { kind: "mle" };
	}

	if (
		amount - FUDGE <= a.taxpayerMle - ledger.mle &&
		post - FUDGE <= t.second
	) {
		return { kind: "taxpayerMle" };
	}

	const mleRemaining = Math.max(0, a.mle - ledger.mle);
	if (post - FUDGE > t.second) {
		return {
			error: `Your payroll would be over the second apron (${fmt(t.second)}) — teams that deep into the tax can only sign free agents to minimum contracts.`,
		};
	}
	if (post - FUDGE > t.first) {
		return {
			error: `This contract exceeds your Taxpayer Mid-Level Exception (${fmt(Math.max(0, a.taxpayerMle - ledger.mle))}) — going over the first apron shrinks the MLE.`,
		};
	}
	return {
		error: `This contract exceeds your remaining Mid-Level Exception (${fmt(mleRemaining)}).`,
	};
};

// Every exception this team could use right now, each with the largest single contract
// (thousands) signable with it. The two MLE flavors collapse into whichever is operative at the
// team's payroll, so the list is at most [MLE variant, BAE] over the cap or [room] under it —
// exactly what a "here's what you can spend" UI line should show.
export const listAvailableExceptions = ({
	payroll,
	ledger,
	usedBaeLastSeason,
}: {
	payroll: number;
	ledger: SigningExceptionLedger;
	usedBaeLastSeason: boolean;
}): { kind: SigningExceptionKind; amount: number }[] => {
	if (!signingExceptionsActive()) {
		return [];
	}

	const t = apronThresholds();
	const a = exceptionAmounts();
	const options: { kind: SigningExceptionKind; amount: number }[] = [];

	if (payroll < g.get("salaryCap")) {
		if (ledger.mle <= 0 && ledger.bae <= 0) {
			options.push({
				kind: "room",
				amount: Math.min(a.room - ledger.room, t.first - payroll),
			});
		}
	} else if (ledger.room <= 0) {
		const mle = {
			kind: "mle" as const,
			amount: Math.min(a.mle - ledger.mle, t.first - payroll),
		};
		const taxpayerMle = {
			kind: "taxpayerMle" as const,
			amount: Math.min(a.taxpayerMle - ledger.mle, t.second - payroll),
		};
		options.push(taxpayerMle.amount > mle.amount ? taxpayerMle : mle);
		if (!usedBaeLastSeason) {
			options.push({
				kind: "bae",
				amount: Math.min(a.bae - ledger.bae, t.first - payroll),
			});
		}
	}

	return options.filter((o) => o.amount > 0);
};

// The most generous exception this team could use right now: its kind and the largest single
// contract (thousands) signable with it. undefined when nothing is available.
export const bestAvailableException = (args: {
	payroll: number;
	ledger: SigningExceptionLedger;
	usedBaeLastSeason: boolean;
}): { kind: SigningExceptionKind; amount: number } | undefined => {
	let best: { kind: SigningExceptionKind; amount: number } | undefined;
	for (const option of listAvailableExceptions(args)) {
		if (!best || option.amount > best.amount) {
			best = option;
		}
	}
	return best;
};

// The largest single contract (thousands) this team could sign right now via any exception.
// Used for AI signing decisions and UI affordability hints; 0 when nothing is available.
export const maxExceptionSigning = (args: {
	payroll: number;
	ledger: SigningExceptionLedger;
	usedBaeLastSeason: boolean;
}): number => bestAvailableException(args)?.amount ?? 0;

// Read a team's usage ledger for the current season, plus whether it used the BAE last season
// (the bi-annual cooldown).
export const getExceptionLedger = async (tid: number) => {
	const season = g.get("season");
	const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
		tid,
		season,
	]);
	const prev = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
		tid,
		season - 1,
	]);

	return {
		ledger: {
			...EMPTY_EXCEPTION_LEDGER,
			...ts?.sigExceptions,
		},
		usedBaeLastSeason: (prev?.sigExceptions?.bae ?? 0) > 0,
	};
};

// Record a signing against the team's ledger. taxpayerMle and mle share a bucket.
export const chargeException = async (
	tid: number,
	kind: SigningExceptionKind,
	amount: number,
) => {
	const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
		tid,
		g.get("season"),
	]);
	if (!ts) {
		return;
	}

	const bucket = kind === "taxpayerMle" ? "mle" : kind;
	ts.sigExceptions = {
		...EMPTY_EXCEPTION_LEDGER,
		...ts.sigExceptions,
	};
	ts.sigExceptions[bucket] += amount;
	await idb.cache.teamSeasons.put(ts);
};
